/* eslint-disable arrow-body-style */
import { type ComponentPropsWithoutRef, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from './ErrorMessage';
import {
  containsReservedCharacters,
  getInputRules,
  stringToObject,
  validateField,
} from '../../utils/form-utils';
import { textinputMaxLength } from '../../constants';
import Input from './TextInput';
import type { ErrorMessages, Rules } from '../../config/types';
import Details from './Details';
import type { Question } from '../../config/types';

type Props = {
  name: string;
  title: string;
  width?: 'full' | 'three-quarters' | 'two-thirds' | 'one-half' | 'one-third' | 'one-quarter';
  rules?: Rules;
  errorMessages?: ErrorMessages;
  detailsHeading?: string;
  detailsContent?: string;
  autoFill?: {
    keys: string[];
    field: string;
  };
  autofillMath?: {
    expression: string;
    fields: {
      [key: string]: string;
    };
  };
} & ComponentPropsWithoutRef<'input'>;

const TextInputField = (props: Props) => {
  const {
    name,
    title,
    rules,
    disabled,
    defaultValue,
    errorMessages,
    detailsHeading,
    detailsContent,
    autoFill,
    autofillMath, // safe to pass down later
  } = props;

  const { register, getFieldState, getValues, setValue, watch } = useFormContext();

  let type = 'text';
  if (rules?.number) type = 'number';
  else if (rules?.email) type = 'email';

  const inputRules = useMemo(
    () => ({ ...getInputRules(rules ?? {}, title, textinputMaxLength) }),
    [rules, title],
  );

  const allFields = getValues();
  const formValues = watch(Object.keys(allFields).filter((key) => key !== name));

  useEffect(() => {
    const allFields = getValues();
    console.log('[TextInputField] Form values at effect trigger:', allFields);

    if (!autoFill) return;

    const valueKey = Object.keys(allFields).find((x) => x.endsWith(autoFill.field));
    if (!valueKey) return;

    const value = allFields[valueKey];
    if (!value) return;

    const values = stringToObject(value);
    if (!values) return;

    const total = Object.values(values).reduce((acc, curr) => acc + Number(curr), 0);
    setValue(name, total);
  }, [formValues, autoFill]);

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue ?? '');
    }
  }, [name, defaultValue, getValues, setValue]);

  const hooksFormRegister = register(name, {
    validate: (value: string) => validateField(title, inputRules, value, errorMessages),
  });

  const { error } = getFieldState(name);

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <Input
        {...props}
        {...hooksFormRegister}
        disabled={(disabled || !!autoFill || !!autofillMath) ?? false}
        onKeyDown={(e) => {
          if (
            e.key.length === 1 &&
            containsReservedCharacters(e.key, inputRules.reservedCharacters)
          )
            e.preventDefault();
        }}
        onPaste={(e) => {
          const clipboardData = e.clipboardData.getData('text');
          if (containsReservedCharacters(clipboardData, inputRules.reservedCharacters))
            e.preventDefault();
        }}
        numeric={!!rules?.number}
        type={type}
      />
    </>
  );
};

TextInputField.displayName = 'TextInputField';

export default TextInputField;

